import { ToasterToast } from '@ais-ui/ui-core';
import {
  classifyApiError,
  getErrorToastContent,
  markErrorHandledLocally,
} from '@ais-ui/custom-hooks';
import { capitalizeFirstLetter } from './capitalizeFirstLetter';
import { Messages } from '@ais-ui/locale-management';

type MutationType = 'create' | 'edit' | 'delete' | 'change' | 'send';

/** Strips C++ source file references (e.g. "webservice.cpp:1570") from backend error messages */
export const sanitizeErrorMessage = (message: string): string =>
  message.replace(/\b\w+\.(?:cpp|c|h|hpp):\d+\s*/gi, '').trim();

export const readErrorBody = async (error: any) => {
  try {
    // First check if ApiClientWrapper already parsed the body
    if (error.response?.parsedBody) {
      return error.response.parsedBody;
    }

    // Fallback to reading the raw response body (for cases where ApiClientWrapper didn't parse it)
    const responseBody = error.response?.body;

    if (responseBody instanceof ReadableStream) {
      const reader = responseBody.getReader();
      const { value } = await reader.read();
      const text = new TextDecoder().decode(value);
      const parsed = JSON.parse(text);
      return parsed;
    }

    return null;
  } catch (e) {
    console.error('Error reading error body:', e);
    return null;
  }
};

const mutationPastTense: Record<MutationType, string> = {
  create: 'created',
  edit: 'edited',
  delete: 'deleted',
  change: 'changed',
  send: 'sent',
};

/** Builds the user-facing title/description for a failed mutation, so the same
 * wording can be reused for toasts and for inline (in-dialog) error messages. */
export const getMutationErrorMessage = (
  error: any,
  messages: Messages,
  mutation: MutationType,
  entity: string,
  customErrorMessages?: { [key: number]: string },
): { title: string; description: string } => {
  const statusCode = error.response?.status;
  const customErrorMessage = customErrorMessages?.[statusCode];

  // 401s aren't localized (matches the global toast shown for read-only
  // queries — see `@ais-ui/custom-hooks/errorHandling`), so a session expiry
  // reads identically everywhere in the app, regardless of whether it was
  // hit via a mutation or a query.
  if (statusCode === 401) {
    const { title, description } = getErrorToastContent(
      classifyApiError(error),
    );
    return { title, description };
  }

  if (statusCode === 403) {
    return {
      title: messages.genericToastTitleForbidden,
      description: messages.genericToastPermissionErrorMessage
        .replace('{mutation}', mutation)
        .replace('{entity}', entity),
    };
  }

  if (statusCode === 409) {
    return {
      title: messages.genericToastTitleError,
      description:
        messages.genericToastGenericErrorDuplicatedDataMessage.replace(
          '{entity}',
          entity,
        ),
    };
  }

  return {
    title: messages.genericToastTitleError,
    description:
      customErrorMessage ||
      messages.genericToastGenericErrorMessage
        .replace('{mutation}', mutation)
        .replace('{entity}', entity),
  };
};

export const mutateCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  entity: string,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
  customErrorMessages?: { [key: number]: string },
) => {
  const successClassName = `${mutation}-${entity}-success-toast`;
  const errorClassName = `${mutation}-${entity}-error-toast`;

  const successMessage = capitalizeFirstLetter(
    messages.genericToastGenericSuccessMessage
      .replace('{mutationPastTense}', mutationPastTense[mutation])
      .replace('{entity}', entity),
  );

  return {
    onSuccess: () => {
      toast({
        className: successClassName,
        title: messages.genericToastTitleSuccess,
        description: successMessage,
      });
      if (setShowDialog) {
        setShowDialog(false);
      }
    },
    onError: (error: any) => {
      // Mark first (synchronously): the global MutationCache error handler
      // defers its toast one macrotask and skips errors marked here, so this
      // local toast is never duplicated by the global one.
      markErrorHandledLocally(error);
      const { title, description } = getMutationErrorMessage(
        error,
        messages,
        mutation,
        entity,
        customErrorMessages,
      );
      toast({
        className: errorClassName,
        title,
        description,
        variant: 'destructive',
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  };
};

export const shiplistCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
  setInvalidIndices?: React.Dispatch<
    React.SetStateAction<{ imo: number[]; mmsi: number[] }>
  >,
  setValidationErrorMessages?: React.Dispatch<
    React.SetStateAction<Record<number, string>>
  >,
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shiplist',
    setIsSubmitting,
    setShowDialog,
  );

  const extendedOnError = async (error: any) => {
    // Mark before any await (see mutateCallbacks.onError for why).
    markErrorHandledLocally(error);
    const statusCode = error.response?.status;
    const errorMessage = error.response?.message;

    if (statusCode === 400) {
      if (errorMessage?.includes('Invalid MMSI number, must be 9 digits')) {
        toast({
          title: messages.genericToastTitleError,
          description: 'Invalid MMSI number, must be 9 digits',
          variant: 'destructive',
        });
        return;
      }
    }

    if (statusCode === 409) {
      try {
        const errorData = await readErrorBody(error);

        if (errorData?.details?.validation_errors) {
          const validationErrors = errorData.details.validation_errors;
          const errorDescriptions = validationErrors.map(
            (validationError: any) => {
              const { reason, row_numbers } = validationError;
              const rowText =
                row_numbers?.length > 1
                  ? `rows ${row_numbers.join(', ')}`
                  : `row ${row_numbers[0]}`;
              return `${reason} (${rowText})`;
            },
          );

          toast({
            title: messages.genericToastTitleError,
            description: errorDescriptions.join('\n'),
            variant: 'destructive',
          });

          // Highlight the problematic rows and set tooltip messages
          if (setInvalidIndices && setValidationErrorMessages) {
            const invalidMmsiIndices: number[] = [];
            const invalidImoIndices: number[] = [];
            const errorMessages: Record<number, string> = {};

            validationErrors.forEach((validationError: any) => {
              const { reason, row_numbers } = validationError;

              // Convert from 1-based (API) to 0-based (UI) row numbers
              const zeroBasedRowNumbers = row_numbers.map(
                (rowNum: number) => rowNum - 1,
              );

              // Store error message for each affected row
              zeroBasedRowNumbers.forEach((rowIndex: number) => {
                errorMessages[rowIndex] = reason;
              });

              if (reason.toLowerCase().includes('mmsi')) {
                invalidMmsiIndices.push(...zeroBasedRowNumbers);
              } else if (reason.toLowerCase().includes('imo')) {
                invalidImoIndices.push(...zeroBasedRowNumbers);
              } else {
                // For other validation errors, treat as MMSI errors for now
                invalidMmsiIndices.push(...zeroBasedRowNumbers);
              }
            });

            setInvalidIndices({
              mmsi: [...new Set(invalidMmsiIndices)], // Remove duplicates
              imo: [...new Set(invalidImoIndices)], // Remove duplicates
            });

            setValidationErrorMessages(errorMessages);
          }

          return;
        }
      } catch (parseError) {
        console.error('Failed to parse 409 error details:', parseError);
      }
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const shapeCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  setShowDialog?: (showDialog: boolean) => void,
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'shape',
    setIsSubmitting,
    setShowDialog,
  );

  const extendedOnError = async (error: any) => {
    // Mark before any await (see mutateCallbacks.onError for why).
    markErrorHandledLocally(error);
    const statusCode = error.response?.status;
    const errorData = await readErrorBody(error);

    if (statusCode === 400 && errorData?.message?.includes('self-crossings')) {
      toast({
        title: messages.genericToastTitleError,
        description: 'GeoJSON contains self-crossings',
        variant: 'destructive',
      });
      return;
    }

    callBacks.onError(error);
  };

  const extendedCallbacks = {
    ...callBacks,
    onError: extendedOnError,
  };

  return extendedCallbacks;
};

export const filterCallbacks = (
  toast: (props: Omit<ToasterToast, 'id'>) => {
    id: string;
    dismiss: () => void;
    update: (props: ToasterToast) => void;
  },
  messages: Messages,
  mutation: MutationType,
  setIsSubmitting: (isSubmitting: boolean) => void,
  reApplyFilters: () => void,
  setShowDialog?: (showDialog: boolean) => void,
) => {
  const callBacks = mutateCallbacks(
    toast,
    messages,
    mutation,
    'filter',
    setIsSubmitting,
    setShowDialog,
  );

  const extendedOnSuccess = async () => {};

  const extendedCallbacks = {
    ...callBacks,
    onSuccess: extendedOnSuccess,
  };

  return extendedCallbacks;
};
